import React, { useState } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
} from '@heroui/react';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:8080';

const FeedbackModal = ({ isOpen, onClose }) => {
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Detect mobile for scroll lock fix
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
    ('ontouchstart' in window) ||
    (window.innerWidth <= 768);

  const inputClasses = {
    inputWrapper:
      "border-default-300 bg-default-50 dark:bg-default-50/20 group-data-[focus=true]:border-primary data-[hover=true]:border-primary",
    label: "text-gray-900 dark:text-white font-semibold text-sm",
    input: "text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400",
  };

  const buttonClasses = "bg-foreground/10 dark:bg-foreground/20";

  const categories = [
    { key: "bug", label: "Bug Report" },
    { key: "feature", label: "Feature Request" },
    { key: "improvement", label: "Improvement Suggestion" },
    { key: "question", label: "Question" },
    { key: "other", label: "Other" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!category) {
      setError('Please select a category');
      return;
    }

    if (!subject.trim() || subject.trim().length < 5) {
      setError('Subject must be at least 5 characters');
      return;
    }

    if (!message.trim() || message.trim().length < 10) {
      setError('Message must be at least 10 characters');
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    try {
      setLoading(true);

      // Send feedback to backend API
      const response = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category,
          subject,
          message,
          email,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to send feedback');
      }

      setSuccess(true);

      // Reset form after 2 seconds and close
      setTimeout(() => {
        setCategory('');
        setSubject('');
        setMessage('');
        setEmail('');
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setCategory('');
      setSubject('');
      setMessage('');
      setEmail('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  // On mobile, render as normal page component - NO modal
  if (isMobile && isOpen) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-full max-w-md p-4 pb-8">
          <div className="flex w-full flex-col gap-4">
          <p className="text-xl sm:text-2xl font-bold pb-2 text-gray-900 dark:text-white">Send Feedback</p>

          <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">
            We'd love to hear your thoughts! Share bugs, feature requests, or general feedback.
          </p>

          {error && (
            <div className="bg-danger-50/50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm backdrop-blur-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-success-50/50 border border-success-200 text-success-700 px-4 py-3 rounded-lg text-sm backdrop-blur-sm">
              Thank you! Your feedback has been submitted successfully.
            </div>
          )}

          <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
            {/* Native select for mobile - triggers iOS picker wheel */}
            <div className="flex flex-col gap-2">
              <label className="text-sm text-foreground font-semibold">
                Category <span className="text-danger">*</span>
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={loading}
                required
                className="
                  w-full px-3 py-2 text-base rounded-lg
                  border-2 border-default-300
                  bg-default-50 text-foreground
                  focus:outline-none focus:border-primary
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.key} value={cat.key}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <Input
              isRequired
              classNames={{
                ...inputClasses,
                input: isMobile ? "text-base" : ""
              }}
              label="Subject"
              placeholder="Brief description of your feedback"
              variant="bordered"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={100}
            />

            <Textarea
              isRequired
              classNames={{
                ...inputClasses,
                input: isMobile ? "text-base" : ""
              }}
              label="Message"
              placeholder="Please provide details about your feedback..."
              variant="bordered"
              minRows={4}
              maxRows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={1000}
            />

            <Input
              isRequired
              classNames={{
                ...inputClasses,
                input: isMobile ? "text-base" : ""
              }}
              label="Email"
              placeholder="Your email for follow-up"
              type="email"
              variant="bordered"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <div className="flex gap-2 pt-2">
              <Button
                className={buttonClasses}
                variant="flat"
                onPress={handleClose}
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                className="bg-primary text-primary-foreground"
                type="submit"
                isLoading={loading}
                fullWidth
              >
                Submit Feedback
              </Button>
            </div>
          </form>

          <p className="text-sm text-gray-600 dark:text-gray-400 font-medium text-center pt-2">
            Your feedback helps us improve ClipKite for everyone
          </p>
        </div>
        </div>
      </div>
    );
  }

  // Desktop: use normal Modal
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      placement="center"
      backdrop="blur"
      size="lg"
      scrollBehavior="inside"
      isDismissable={true}
      isKeyboardDismissDisabled={false}
    >
      <ModalContent className="rounded-large bg-white dark:bg-default-100 shadow-large">
        <div className="flex w-full flex-col gap-4 px-8 pt-6 pb-10">
          <p className="text-2xl font-bold pb-2 text-gray-900 dark:text-white">Send Feedback</p>

          <p className="text-base text-gray-700 dark:text-gray-300 font-medium">
            We'd love to hear your thoughts! Share bugs, feature requests, or general feedback.
          </p>

          {error && (
            <div className="bg-danger-50/50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm backdrop-blur-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-success-50/50 border border-success-200 text-success-700 px-4 py-3 rounded-lg text-sm backdrop-blur-sm">
              Thank you! Your feedback has been submitted successfully.
            </div>
          )}

          <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
            <Select
              isRequired
              label="Category"
              placeholder="Select a category"
              variant="bordered"
              classNames={inputClasses}
              selectedKeys={category ? [category] : []}
              onSelectionChange={(keys) => setCategory(Array.from(keys)[0])}
              disableAnimation={isMobile}
              popoverProps={{
                shouldBlockScroll: false,
                placement: "bottom",
                offset: 5
              }}
            >
              {categories.map((cat) => (
                <SelectItem key={cat.key} value={cat.key}>
                  {cat.label}
                </SelectItem>
              ))}
            </Select>

            <Input
              isRequired
              classNames={{
                ...inputClasses,
                input: "text-base"
              }}
              label="Subject"
              placeholder="Brief description of your feedback"
              variant="bordered"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={100}
            />

            <Textarea
              isRequired
              classNames={{
                ...inputClasses,
                input: "text-base"
              }}
              label="Message"
              placeholder="Please provide details about your feedback..."
              variant="bordered"
              minRows={4}
              maxRows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={1000}
            />

            <Input
              isRequired
              classNames={{
                ...inputClasses,
                input: "text-base"
              }}
              label="Email"
              placeholder="Your email for follow-up"
              type="email"
              variant="bordered"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <div className="flex gap-2 pt-2">
              <Button
                className={buttonClasses}
                variant="flat"
                onPress={handleClose}
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                className="bg-primary text-primary-foreground"
                type="submit"
                isLoading={loading}
                fullWidth
              >
                Submit Feedback
              </Button>
            </div>
          </form>

          <p className="text-sm text-gray-600 dark:text-gray-400 font-medium text-center pt-2">
            Your feedback helps us improve ClipKite for everyone
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default FeedbackModal;
